import { useCallback, useEffect, useRef } from 'react';

import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

export function useInboxPreviewScroll() {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getScrollDistance = useCallback((): number => {
    return 150; // 150px of scroll distance
  }, []);

  const getScrollContainer = useCallback((): HTMLElement | null => {
    if (scrollContainerRef.current) {
      return scrollContainerRef.current;
    }

    return document.body;
  }, []);

  const scrollUp = useCallback(() => {
    const container = getScrollContainer();
    if (!container) {
      return;
    }

    const currentScrollTop = container.scrollTop;
    if (currentScrollTop === 0) {
      return;
    }

    const scrollDistance = getScrollDistance();
    container.scrollBy({
      top: -scrollDistance,
      behavior: 'smooth',
    });
  }, [getScrollContainer, getScrollDistance]);

  const scrollDown = useCallback(() => {
    const container = getScrollContainer();
    if (!container) {
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight) {
      return;
    }

    const scrollDistance = getScrollDistance();
    container.scrollBy({
      top: scrollDistance,
      behavior: 'smooth',
    });
  }, [getScrollContainer, getScrollDistance]);

  useEffect(() => {
    registerShortcut({
      key: ShortcutKey.ARROW_UP,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: scrollUp,
      description: 'Scroll email content up',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_DOWN,
      contexts: [ShortcutContext.INBOX_PREVIEW],
      handler: scrollDown,
      description: 'Scroll email content down',
    });

    return () => {
      const ctx = [ShortcutContext.INBOX_PREVIEW];
      unregisterShortcut(ShortcutKey.ARROW_UP, ctx);
      unregisterShortcut(ShortcutKey.ARROW_DOWN, ctx);
    };
  }, [registerShortcut, unregisterShortcut, scrollUp, scrollDown]);

  return { scrollContainerRef };
}
